"use client";

// CreditSuccessModal.jsx
import { useEffect, useRef } from "react";
import { useArtifact } from "@/hooks/use-artifact";

export default function AfterPaymentSuccessModel({
  open,
  onClose,
  creditsAdded = 0,       // e.g., 200
  amountPaid = "₹0",      // e.g., "₹499"
  newBalance = 0,         // e.g., 1200
  txId,                   // optional transaction id
  onPrimary,              // e.g., () => router.push("/playground")
  onSecondary,            // e.g., () => router.push("/billing")
}) {

  const { artifact } = useArtifact();

  // let open = false
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const primaryBtnRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
      // Basic focus trap
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        const list = Array.from(focusables).filter(el => !el.hasAttribute("disabled"));
        if (list.length === 0) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus primary button when opened
  useEffect(() => {
    if (open) {
      // Delay ensures element is mounted
      setTimeout(() => primaryBtnRef.current?.focus(), 0);
      // Optional: Confetti if you use canvas-confetti
      // import confetti from "canvas-confetti"; confetti({ particleCount: 80, spread: 70, origin: { y: 0.3 }});
    }
  }, [open]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      ref={overlayRef}
      onClick={onClose}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="credit-success-title"
        aria-describedby="credit-success-desc"
        ref={dialogRef}
        onClick={stop}
        className="relative w-full max-w-md rounded-2xl bg-white text-gray-900 shadow-2xl ring-1 ring-black/5 transition-transform duration-200 ease-out dark:bg-neutral-900 dark:text-white"
        style={{ transform: "translateY(0)" }}
      >
        {/* Close (X) */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Body */}
        <div className="p-6 pt-8 text-center">
          {/* Success check animation (pure CSS stroke dash trick) */}
          <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <svg viewBox="0 0 52 52" className="h-9 w-9">
              <circle cx="26" cy="26" r="25" fill="none" className="stroke-current text-green-400/30" strokeWidth="2" />
              <path
                d="M14.1 27.2l7.1 7.2 16-16.4"
                fill="none"
                className="success-check"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h2 id="credit-success-title" className="text-2xl font-semibold tracking-tight">
            Congratulations! 🎉
          </h2>

          <p id="credit-success-desc" className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Your purchase was successful and credits have been added to your account.
          </p>

          <div className="mt-5 grid grid-cols-3 gap-3 rounded-xl border border-black/5 p-3 text-sm dark:border-white/10">
            <div className="rounded-lg bg-black/5 p-3 dark:bg-white/5">
              <div className="text-xs text-gray-500 dark:text-gray-400">Credits Added</div>
              <div className="mt-1 text-base font-semibold">{artifact?.paymentDetails?.credit || 0}</div>
            </div>
            <div className="rounded-lg bg-black/5 p-3 dark:bg-white/5">
              <div className="text-xs text-gray-500 dark:text-gray-400">Amount Paid</div>
              <div className="mt-1 text-base font-semibold">{artifact?.paymentDetails?.amount || 0}</div>
            </div>
            <div className="rounded-lg bg-black/5 p-3 dark:bg-white/5">
              <div className="text-xs text-gray-500 dark:text-gray-400">New Balance</div>
              <div className="mt-1 text-base font-semibold">{artifact?.paymentDetails?.newCredit || 0}</div>
            </div>
          </div>

          {artifact?.paymentDetails?.paymentRes?.razorpay_payment_id && (
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Transaction ID: <span className="font-mono">{artifact?.paymentDetails?.paymentRes?.razorpay_payment_id}</span>
            </div>
          )}

          {/* <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              ref={primaryBtnRef}
              onClick={onPrimary}
              className="inline-flex w-full items-center justify-center rounded-xl bg-black px-4 py-2.5 text-white transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-white dark:text-black"
            >
              Start Creating
            </button>
            <button
              onClick={onSecondary}
              className="inline-flex w-full items-center justify-center rounded-xl border border-black/10 px-4 py-2.5 text-gray-800 transition hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-white/15 dark:text-white dark:hover:bg-white/5"
            >
              View Balance
            </button>
          </div> */}

          {/* <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Tip: You can manage invoices anytime in <strong>Billing &amp; Usage</strong>.
          </p> */}
        </div>
      </div>

      {/* Inline keyframes for the check mark */}
      <style jsx>{`
        .success-check {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: dash 650ms ease-out forwards;
        }
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}